import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../App";

export default function Blog({ auth }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");

  const fetchPosts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      
      const response = await fetch(`${API}/blog/posts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts);
        setCategories(data.categories);
      }
    } catch (err) {
      console.error("Failed to fetch posts:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  // Strip HTML tags from excerpt for display
  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="border-b border-[#E8E4DE] bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E6E2DD] to-[#A87C6D]/20 flex items-center justify-center">
                <span className="text-lg">✧</span>
              </div>
              <span className="text-xl font-light text-[#2D2A26]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                The Soft Ritual
              </span>
            </Link>
            <nav className="flex items-center gap-4">
              {auth?.user ? (
                <Link to="/dashboard" className="px-4 py-2 text-sm text-[#4A4744] hover:text-[#2D2A26] transition-colors">
                  Dashboard
                </Link>
              ) : (
                <Link to="/login" className="px-4 py-2 text-sm text-[#A87C6D] hover:text-[#96695C] transition-colors">
                  Sign In
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-[#2D2A26] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            The Ritual Edit
          </h1>
          <p className="text-sm text-[#A87C6D] uppercase tracking-[0.2em] mb-4">
            This is your invitation to read slowly, feel deeply, and reconnect with what grounds you.
          </p>
          <p className="text-lg text-[#4A4744] max-w-2xl mx-auto leading-relaxed">
            The Ritual Edit is our reflective space—blending poetic insight, grounded guidance, and meaningful ritual. Think of it as your digital sanctuary, created to offer comfort, clarity, and reverence in the pause.
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <button
              onClick={() => handleCategoryChange("")}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                !selectedCategory ? "bg-[#2D2A26] text-white" : "bg-[#F5F2EE] text-[#4A4744] hover:bg-[#E8E4DE]"
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${
                  selectedCategory === cat ? "bg-[#A87C6D] text-white" : "bg-[#F5F2EE] text-[#4A4744] hover:bg-[#E8E4DE]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-2 border-[#A87C6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#4A4744]">Loading articles...</p>
          </div>
        )}

        {/* Posts Grid */}
                {!loading && posts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map(post => (
              <Link key={post.post_id} to={`/blog/${post.slug}`} className="group" data-testid={`blog-post-${post.slug}`}>
                <article className="bg-white rounded-2xl border border-[#E8E4DE] overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  {post.featured_image ? (
                    <div className="aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#E6E2DD] to-[#A87C6D]/20">
                      <img 
                        src={post.featured_image.startsWith('http') ? post.featured_image : `${API.replace('/api', '')}${post.featured_image}`} 
                        alt={post.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><span class="text-4xl opacity-50">✧</span></div>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#E6E2DD] to-[#A87C6D]/20 flex items-center justify-center">
                      <span className="text-4xl opacity-50">✧</span>
                    </div>
                  )}
                  
                  <div className="p-6">
                    {post.category && (
                      <span className="text-xs uppercase tracking-wider text-[#A87C6D] font-medium">{post.category}</span>
                    )}
                    <h2 className="text-xl font-medium text-[#2D2A26] mt-2 mb-3 group-hover:text-[#A87C6D] transition-colors line-clamp-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {post.title}
                    </h2>
                    <p className="text-[#5A5652] text-sm line-clamp-3 mb-4">{stripHtml(post.excerpt)}</p>
                    <div className="flex items-center justify-between text-xs text-[#7A7672]">
                      <span>{formatDate(post.published_at)}</span>
                      {post.view_count > 0 && <span>{post.view_count} views</span>}
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && posts.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-[#F5F2EE] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✧</span>
            </div>
            <h3 className="text-lg font-medium text-[#2D2A26] mb-2">No articles yet</h3>
            <p className="text-[#4A4744]">
              {selectedCategory ? `No articles in "${selectedCategory}" category yet.` : "Check back soon for new content."}
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E8E4DE] bg-white/50 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#6E6A66]">© {new Date().getFullYear()} The Soft Ritual</p>
            <div className="flex gap-6">
              <Link to="/about" className="text-sm text-[#4A4744] hover:text-[#A87C6D]">About</Link>
              <Link to="/store" className="text-sm text-[#4A4744] hover:text-[#A87C6D]">Store</Link>
              <Link to="/terms" className="text-sm text-[#4A4744] hover:text-[#A87C6D]">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
